// The upload manager: shared by the whole app, so uploads go on while the user moves between folders and pages.
// Three files at a time, each hashed first (known content is not sent again), then sent in chunks that can be paused,
// resumed (also after a reload, once the same file is picked again) and cancelled. See docs/drive-api.md, Uploads.
import { reactive } from 'vue'
import axios from 'axios'
import { apiError } from './api'

const MAX_PARALLEL = 3
const RESUME_KEY = 'drive-uploads'

const state = reactive({ items: [], panelOpen: false })
const finishedListeners = new Set()
let nextItemId = 1
let worker = null
let nextJobId = 1
const hashJobs = new Map()
let conflictResolver = null
let decisionForAll = null

export function useUploads() {
    return { state, add, pause, resume, cancel, retry, clearFinished, onFinished, setConflictResolver }
}

/**
 * Queues files: [{ file, parentId, name }] for new files, or [{ file, nodeId, name }] for a new version.
 */
function add(entries) {
    for (const entry of entries) {
        state.items.push(reactive({
            id: nextItemId++,
            file: entry.file,
            name: entry.name || entry.file.name,
            parentId: entry.parentId ?? null,
            nodeId: entry.nodeId ?? null,
            size: entry.file.size,
            status: 'queued',
            progress: 0,
            error: null,
            sha256: null,
            uploadId: null,
            paused: false,
            cancelled: false,
            waiters: [],
        }))
    }
    state.panelOpen = true
    pump()
}

function pause(item) {
    item.paused = true
}

function resume(item) {
    item.paused = false
    item.waiters.splice(0).forEach(wake => wake())
}

async function cancel(item) {
    item.cancelled = true
    resume(item)
    item.status = 'cancelled'
    if (item.uploadId) {
        forget(item)
        await axios.delete(`/api/drive/uploads/${item.uploadId}`).catch(() => {})
    }
    pump()
}

function retry(item) {
    Object.assign(item, { status: 'queued', error: null, cancelled: false, paused: false })
    pump()
}

function clearFinished() {
    state.items = state.items.filter(item => !['done', 'skipped', 'cancelled'].includes(item.status))
}

// Called with (item, node) whenever a file is saved, so the open folder can refresh
function onFinished(listener) {
    finishedListeners.add(listener)
    return () => finishedListeners.delete(listener)
}

// The page asks the user what to do about a taken name: async (item) => ({ action: 'replace'|'keep'|'skip', applyToAll })
function setConflictResolver(resolver) {
    conflictResolver = resolver
}

function pump() {
    const busy = state.items.filter(item => ['hashing', 'starting', 'uploading', 'committing'].includes(item.status)).length
    const next = state.items.filter(item => item.status === 'queued').slice(0, Math.max(0, MAX_PARALLEL - busy))
    next.forEach(item => run(item))
    if (!next.length && !busy) {
        decisionForAll = null
    }
}

async function run(item) {
    try {
        item.status = 'hashing'
        item.sha256 ??= await hashFile(item.file, progress => { item.progress = progress * 0.05 })
        if (item.cancelled) return

        item.status = 'starting'
        const session = (await resumable(item)) || (await start(item))
        if (!session) return
        if (session.complete) {
            finish(item, session.node)
            return
        }

        item.uploadId = session.upload_id
        remember(item)
        item.status = 'uploading'
        const received = new Set(session.received_chunks)
        const chunkSize = session.chunk_size
        let sent = received.size * chunkSize

        for (let n = 0; n < session.chunks; n++) {
            if (received.has(n)) continue
            while (item.paused && !item.cancelled) {
                await new Promise(wake => item.waiters.push(wake))
            }
            if (item.cancelled) return

            const blob = item.file.slice(n * chunkSize, Math.min((n + 1) * chunkSize, item.size))
            await axios.put(`/api/drive/uploads/${item.uploadId}/chunks/${n}`, blob, {
                headers: { 'Content-Type': 'application/octet-stream' },
                onUploadProgress: event => { item.progress = 0.05 + 0.95 * Math.min(1, (sent + event.loaded) / Math.max(1, item.size)) },
            })
            sent += blob.size
        }

        item.status = 'committing'
        const { data } = await axios.post(`/api/drive/uploads/${item.uploadId}/commit`)
        forget(item)
        finish(item, data.node)
    } catch (error) {
        if (!item.cancelled) {
            item.status = 'error'
            item.error = apiError(error, error.message)
            if (error.response?.data?.code === 'checksum_mismatch') {
                forget(item)
                item.uploadId = null
            }
        }
    } finally {
        pump()
    }
}

/**
 * Starts the upload; a taken name is the user's call: a new version of the file there, both kept, or skipped.
 */
async function start(item) {
    let keepNumber = 1
    for (;;) {
        try {
            const payload = item.nodeId
                ? { node_id: item.nodeId, size: item.size, sha256: item.sha256 }
                : { parent_id: item.parentId, name: item.name, size: item.size, sha256: item.sha256, client_mtime: new Date(item.file.lastModified).toISOString() }
            const { data } = await axios.post('/api/drive/uploads', payload)
            return data
        } catch (error) {
            const body = error.response?.data
            const conflict = error.response?.status === 409 && body?.code === 'name_conflict' && !item.nodeId
            if (!conflict) {
                // A "replace" that named a folder: keep both instead
                if (item.nodeId && error.response?.status === 422 && item.replacing) {
                    item.nodeId = null
                    item.replacing = false
                    continue
                }
                throw error
            }

            const action = await decide(item)
            if (action === 'skip') {
                item.status = 'skipped'
                return null
            }
            if (action === 'replace') {
                item.nodeId = body.existing_node_id
                item.replacing = true
                continue
            }
            item.name = numbered(item.name, ++keepNumber)
        }
    }
}

async function decide(item) {
    if (decisionForAll) return decisionForAll
    const { action, applyToAll } = conflictResolver ? await conflictResolver(item) : { action: 'keep', applyToAll: false }
    if (applyToAll) decisionForAll = action
    return action
}

// "report.pdf" → "report (2).pdf"
function numbered(name, n) {
    const base = name.replace(/ \(\d+\)(\.[^.]*)?$/, '$1')
    const dot = base.lastIndexOf('.')
    return dot > 0 ? `${base.slice(0, dot)} (${n})${base.slice(dot)}` : `${base} (${n})`
}

function finish(item, node) {
    item.status = 'done'
    item.progress = 1
    item.file = null
    finishedListeners.forEach(listener => listener(item, node))
}

// --- Resuming after a reload: the session id is kept per file until it is committed ---

function resumeKey(item) {
    return [item.parentId ?? `v${item.nodeId}`, item.name, item.size, item.file.lastModified, item.sha256].join('|')
}

function remembered() {
    try {
        return JSON.parse(localStorage.getItem(RESUME_KEY) || '{}')
    } catch {
        return {}
    }
}

function remember(item) {
    try {
        localStorage.setItem(RESUME_KEY, JSON.stringify({ ...remembered(), [resumeKey(item)]: item.uploadId }))
    } catch {
        // Without storage, an interrupted upload simply starts again
    }
}

function forget(item) {
    try {
        const all = remembered()
        delete all[resumeKey(item)]
        localStorage.setItem(RESUME_KEY, JSON.stringify(all))
    } catch {
        // see remember()
    }
}

async function resumable(item) {
    const uploadId = remembered()[resumeKey(item)]
    if (!uploadId) return null
    try {
        const { data } = await axios.get(`/api/drive/uploads/${uploadId}`)
        return data
    } catch {
        forget(item)
        return null
    }
}

// --- Hashing in a worker ---

function hashFile(file, onProgress) {
    if (!worker) {
        worker = new Worker(new URL('./hashWorker.js', import.meta.url), { type: 'module' })
        worker.onmessage = ({ data }) => {
            const job = hashJobs.get(data.id)
            if (!job) return
            if (data.progress !== undefined) job.onProgress(data.progress)
            if (data.sha256) {
                hashJobs.delete(data.id)
                job.resolve(data.sha256)
            }
            if (data.error) {
                hashJobs.delete(data.id)
                job.reject(new Error(data.error))
            }
        }
    }
    return new Promise((resolve, reject) => {
        const id = nextJobId++
        hashJobs.set(id, { resolve, reject, onProgress })
        worker.postMessage({ id, file })
    })
}
